import apiClient from './client'
import { SERVICE_NAME } from '@/config/api'
import {
  DreamityUserLoginResDto,
  DreamityUserSearchResDto,
  DreamityUserModifyReqDto,
} from '@/store/types/user.types'
import type { AdRewardStatusResDto } from '@/store/types/point.types'
import { getFieldFromToken } from '@/utils/jwt'

/** 공통 회원 API 응답 → 앱 프로필 타입 (PK는 응답에 없음 — 본인 프로필은 JWT userId로 보완) */
function mapCommonUserToProfile(row: Record<string, unknown>): DreamityUserSearchResDto {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
  const jwtUserId = token ? (getFieldFromToken(token, 'userId') as number | undefined) : undefined
  const jwtEmail = token ? (getFieldFromToken(token, 'email') as string | undefined) : undefined
  const rowEmail = String(row.email ?? '')
  const explicit = (row.id ?? row.userId) as number | undefined
  const userId =
    explicit ??
    (jwtUserId != null && jwtEmail && rowEmail && jwtEmail === rowEmail ? jwtUserId : undefined)
  const proRaw = row.proExpireDate ?? row.pro_expire_date
  return {
    id: userId,
    userId,
    username: (row.name as string) ?? '',
    email: (row.email as string) ?? '',
    role: (row.role as string) ?? 'NORMAL',
    userType: (row.userType as string) ?? 'USER',
    proExpireDate: proRaw != null ? String(proRaw) : undefined,
  }
}

/**
 * 회원: `/api/common/user` (common_user, JWT userId = common PK).
 * DB 마이그레이션 후 dreamity_* 의 user_id도 동일 PK를 가리키도록 맞춰야 함.
 */
export const userApi = {
  fetchCurrentUser: async (): Promise<DreamityUserSearchResDto> => {
    const response = await apiClient.get('/common/user/detail', {
      params: { serviceName: SERVICE_NAME },
    })
    return mapCommonUserToProfile(response.data as Record<string, unknown>)
  },

  fetchAllUsers: async (): Promise<DreamityUserSearchResDto[]> => {
    const response = await apiClient.get('/common/user/all', {
      params: { serviceName: SERVICE_NAME },
    })
    const list = response.data as Record<string, unknown>[]
    return Array.isArray(list) ? list.map(mapCommonUserToProfile) : []
  },

  updateUser: async (userData: DreamityUserModifyReqDto): Promise<void> => {
    await apiClient.put('/common/user', {
      name: userData.name,
      email: userData.email,
      password: '',
      serviceName: SERVICE_NAME,
    })
  },

  deleteUser: async (): Promise<void> => {
    await apiClient.delete('/common/user')
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/common/user/logout')
  },

  /** tori-todo와 동일: 보상형 광고 PRO 연장 한도·쿨타임 */
  getAdRewardStatus: async (): Promise<AdRewardStatusResDto> => {
    const response = await apiClient.get<AdRewardStatusResDto>('/common/user/ad-reward-status', {
      params: { serviceName: SERVICE_NAME },
    })
    return response.data
  },

  /** tori-todo `upgradeToProByAd` — receiptData=AD_REWARD 시 PRO 1시간 누적 */
  upgradeToProByAd: async (): Promise<void> => {
    await apiClient.post('/common/user/upgrade-pro', {
      receiptData: 'AD_REWARD',
    })
  },

  getGoogleLoginUrl: async (service: string = SERVICE_NAME, guestId?: string): Promise<string> => {
    const params = new URLSearchParams({ service })
    if (guestId) {
      params.append('guestId', guestId)
    }
    const response = await apiClient.get(`/auth/google/url?${params.toString()}`)
    return response.data.authUrl
  },

  googleLogin: async (
    code: string,
    service: string = SERVICE_NAME,
    guestId?: string
  ): Promise<
    DreamityUserLoginResDto & {
      tokenType?: string
      userId?: number
      email?: string
      name?: string
      role?: string
    }
  > => {
    const response = await apiClient.post('/auth/google/login', {
      code,
      service,
      guestId,
    })
    return response.data
  },

  guestLogin: async (guestId?: string): Promise<DreamityUserLoginResDto> => {
    const params = new URLSearchParams({ serviceName: SERVICE_NAME })
    if (guestId) {
      params.append('guestId', guestId)
    }
    const response = await apiClient.post(`/common/user/guest?${params.toString()}`)
    return response.data
  },

  googleLoginWithToken: async (
    idToken: string,
    service: string = SERVICE_NAME,
    guestId?: string
  ): Promise<
    DreamityUserLoginResDto & {
      tokenType?: string
      userId?: number
      email?: string
      name?: string
      role?: string
    }
  > => {
    const body: Record<string, string> = {
      idToken,
      service,
    }
    if (guestId) {
      body.guestId = guestId
    }
    const response = await apiClient.post('/auth/google/token', body, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    })
    return response.data
  },

  appleLoginWithToken: async (
    identityToken: string,
    service: string = SERVICE_NAME,
    name?: string,
    guestId?: string
  ): Promise<
    DreamityUserLoginResDto & {
      tokenType?: string
      userId?: number
      email?: string
      name?: string
      role?: string
    }
  > => {
    const body: {
      identityToken: string
      service: string
      name?: string
      guestId?: string
    } = {
      identityToken,
      service,
    }
    if (name != null && name !== '') {
      body.name = name
    }
    if (guestId) {
      body.guestId = guestId
    }
    const response = await apiClient.post('/auth/apple/token', body, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    })
    return response.data
  },
}
